import { FileWithInfo } from '@aiwyn/api';
import {
  CxpDeliverableContact,
  CxpDeliverableDetail,
  CxpDeliverableFile,
  CxpDeliverableTeamMember,
} from '../../models';
import {
  CxpDeliverableApiResponse,
  CxpDeliverableStaffResponse,
} from '../cxp-deliverable-api.service';

export interface AssembleDeliverableDetailArgs {
  apiResponse: CxpDeliverableApiResponse;
  filesWithInfo: FileWithInfo[];
  serviceLineName: string | null;
  clientName: string | null;
}

/**
 * Picks the contact's display email per Experience/DMS convention:
 *   1. `contact.username` (treated as the primary email address)
 *   2. First **non-empty** entry of `contact_info.email` after splitting on
 *      `,` — the legacy field can hold multiple comma-joined addresses, and
 *      a leading `,` would otherwise leave us with an empty `[0]`.
 *
 * Returns `null` when neither source yields a usable address.
 */
function pickContactEmail(
  contact: { username?: string | null; contact_info?: { email?: string | null } | null } | null | undefined,
): string | null {
  const username = contact?.username?.trim();
  if (username) return username;

  const raw = contact?.contact_info?.email;
  if (!raw) return null;
  for (const part of raw.split(',')) {
    const trimmed = part.trim();
    if (trimmed.length > 0) return trimmed;
  }
  return null;
}

/**
 * Pure mappers from the API surface to the CXP-owned `CxpDeliverableDetail`
 * model. No DI, no API calls — just data transformation.
 */
export class DeliverableDetailMappers {
  static toFile(file: FileWithInfo): CxpDeliverableFile {
    return {
      id: file.id,
      name: file.name,
      sizeBytes: file.fileInfo?.size ?? null,
      uploadedAt: file.fileInfo?.createdAt ?? null,
      dmsId: file.dmsId ?? null,
    };
  }

  static toContact(
    raw: CxpDeliverableApiResponse['deliverableContacts'][number],
  ): CxpDeliverableContact | null {
    const email = pickContactEmail(raw.contact);
    if (!email) {
      // No email = nothing to render. Skip rather than show a broken row.
      return null;
    }

    const fullName = raw.contact?.contact_info?.full_name?.trim() ?? '';

    return {
      contactId: raw.contactId,
      fullName: fullName.length > 0 ? fullName : null,
      email,
    };
  }

  /**
   * Maps a raw staff record into a team member row. Returns `null` when the
   * row has no email — the UI keys off email (mailto link) so a row without
   * one isn't actionable.
   */
  static toTeamMember(staff: CxpDeliverableStaffResponse | null): CxpDeliverableTeamMember | null {
    if (!staff) return null;

    const email = staff.email?.trim();
    if (!email) return null;

    const first = staff.firstName?.trim() ?? '';
    const last = staff.lastName?.trim() ?? '';
    const fullName = [first, last].filter((part) => part.length > 0).join(' ').trim();

    return {
      staffId: staff.id,
      // Fallback to email's local-part for display when staff has no name on
      // file — keeps the row legible instead of a blank line.
      fullName: fullName.length > 0 ? fullName : email.split('@')[0],
      email,
    };
  }

  /**
   * Builds the Team sidebar list per AC #15: shows the firm staff who
   * created the deliverable AND the staff who last updated it (the resender,
   * if different). Deduped by `staffId` so a single sender appears once.
   * Order: original sender first, resender second.
   */
  static toTeamMembers(apiResponse: CxpDeliverableApiResponse): CxpDeliverableTeamMember[] {
    const created = DeliverableDetailMappers.toTeamMember(apiResponse.createdByStaff);
    const updated = DeliverableDetailMappers.toTeamMember(apiResponse.lastUpdatedByStaff);

    const result: CxpDeliverableTeamMember[] = [];
    const seenIds = new Set<number>();

    for (const member of [created, updated]) {
      if (member && !seenIds.has(member.staffId)) {
        result.push(member);
        seenIds.add(member.staffId);
      }
    }

    return result;
  }

  static toDetail({
    apiResponse,
    filesWithInfo,
    serviceLineName,
    clientName,
  }: AssembleDeliverableDetailArgs): CxpDeliverableDetail {
    const filesById = new Map(filesWithInfo.map((file) => [file.id, file]));
    const orderedFileIds = apiResponse.fileIds.map((entry) => entry.fileId);
    const files = orderedFileIds
      .map((id) => filesById.get(id))
      .filter((file): file is FileWithInfo => file !== undefined)
      .map(DeliverableDetailMappers.toFile);

    const clientContacts = apiResponse.deliverableContacts
      .map(DeliverableDetailMappers.toContact)
      .filter((contact): contact is CxpDeliverableContact => contact !== null);

    return {
      id: apiResponse.id,
      name: apiResponse.name,
      description: apiResponse.description ?? '',
      serviceLineName,
      clientName,
      files,
      clientContacts,
      teamMembers: DeliverableDetailMappers.toTeamMembers(apiResponse),
    };
  }
}
